import { useState, useEffect, useCallback, useMemo } from "react";
import apiClient from "utils/apiClient";

const useTasks = () => {
  const authToken = sessionStorage.getItem("authToken");

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    }),
    [authToken]
  );

  const [state, setState] = useState({
    tasks: [],
    loading: false,
    error: null,
    pagination: {
      page: 0,
      size: 10,
      totalElements: 0,
      totalPages: 0,
      first: true,
      last: false,
    },
  });

  /** Generic GET helper **/
  const getRequest = useCallback(async (url) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.get(url, { headers });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[useTasks:getRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw new Error(errorMessage);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [headers]);

  /** Generic PUT helper **/
  const putRequest = useCallback(async (url, payload) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.put(url, payload, { headers });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[useTasks:putRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw new Error(errorMessage);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [headers]);

  /** Generic DELETE helper **/
  const deleteRequest = useCallback(
    async (url, data = null) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiClient.delete(url, {
          data,
          headers,
        });
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        console.error("[useTasks:deleteRequest]", errorMessage);
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [headers]
  );

  /** Fetch all tasks with pagination **/
  const fetchTasks = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const pageSize = 50;
      const Url = "/olitask-service/api/v1/tasks";

      // Fetch first page
      const firstResponse = await apiClient.get(
        `${Url}?page=0&size=${pageSize}&refresh=true`
      );

      const { content: firstContent = [], totalPages = 1} =
        firstResponse.data;
      let allTasks = [...firstContent];

      // Fetch remaining pages if any
      if (totalPages > 1) {
        const promises = [];
        for (let p = 1; p < totalPages; p++) {
          promises.push(
            apiClient.get(`${Url}?page=${p}&size=${pageSize}&refresh=true`, {
              headers,
            })
          );
        }
        const responses = await Promise.all(promises);
        responses.forEach((res) => {
          allTasks = allTasks.concat(res.data.content || []);
        });
      }

      const formattedTasks = allTasks.map(formatTaskData);

      setState({
        tasks: formattedTasks,
        loading: false,
        error: null,
        pagination: {
          page: 0,
          size: formattedTasks.length,
          totalElements:formattedTasks.length,
          totalPages,
          first: true,
          last: true,
        },
      });
    } catch (err) {
      console.error("[useTasks] Error fetching tasks:", err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to load tasks. Please try again later.",
      }));
    }
  }, [headers]);

  /** Fetch single task by ID **/
  const getTaskById = useCallback(
    async (taskId) => {
      if (!taskId) throw new Error("Task ID is required");
      const data = await getRequest(`/olitask-service/api/v1/tasks/${taskId}`);
      return formatTaskData(data);
    },
    [getRequest]
  );

  /** ✅ Update Task Status **/
  const updateTaskStatus = useCallback(
    async (taskId, status) => {
      if (!taskId) throw new Error("Task ID is required");
      const result = await putRequest(
        `/olitask-service/api/v1/tasks/${taskId}/status`,
        { status }
      );
      await fetchTasks(); // refresh list
      return result;
    },
    [putRequest, fetchTasks]
  );

  /** Deactivate Task **/
  const deactivateTask = useCallback(
    async (taskId) => {
      if (!taskId) throw new Error("Task ID is required");
      const result = await putRequest(
        `/olitask-service/api/v1/tasks/${taskId}/deactivate`
      );
      await fetchTasks();
      return result;
    },
    [fetchTasks, putRequest]
  );

  /** Soft Delete Task **/
  const deleteTask = useCallback(
    async (taskId) => {
      if (!taskId) throw new Error("Task ID is required");
      const result = await deleteRequest(
        `/olitask-service/api/v1/tasks/${taskId}?type=SOFT`
      );
      await fetchTasks();
      return result;
    },
    [fetchTasks, deleteRequest]
  );

  /** Auto-fetch tasks on mount **/
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  /** Return state and methods **/
  const { tasks, loading, error, pagination } = state;

  return {
    tasks,
    loading,
    error,
    pagination,
    fetchTasks,
    refetch: fetchTasks,

    /** Derived counts **/
    totalTasks: pagination.totalElements,
    completedTasks: tasks.filter((t) => t.status === "COMPLETED").length,
    activeTasks: tasks.filter((t) => t.status === "ACTIVE").length,
    pendingTasks: tasks.filter((t) => t.status === "PENDING").length,

    /** Methods **/
    getTaskById,
    updateTaskStatus,
    deactivateTask,
    deleteTask,
  };
};

/** Helper to format task data **/
function formatTaskData(task = {}) {
  return {
    id: task.id || "None",
    title: task.title || "Untitled",
    description: task.description || "No description",
    status: task.status || "UNKNOWN",
    priority: task.priority || "NORMAL",
    assignedTo: task.assignedTo || null,
    requester: task.requester || null,
    helper: task.helper || null,
    taskCharges: task.taskCharges || {},
    createdAt: task.createdAt || null,
    updatedAt: task.updatedAt || null,
    photos: task.photos || [],
    location: task.location || {},
  };
}

export default useTasks;
